import { type ParcelRow, canonicalizeStreet } from '@mollusk/shared';
import { pointInGeometry, wktToGeoJSON } from './wkt';

/**
 * Choose which parcel in a bbox query corresponds to the geocoded
 * address. `/api/parcels` returns rows in BigQuery's natural (unordered)
 * order, so the previous "take parcels[0]" approach picked the wrong
 * parcel whenever the bbox caught a neighbor — e.g. 4805 Avenue G next
 * door to 4810 Avenue H.
 *
 * Prefer address-line match (canonicalized so Mapbox's "Avenue" lines up
 * with the parcel table's "AVE"), then fall back to whichever polygon
 * actually contains the geocoded point. If neither check fires, return
 * null rather than confidently highlighting an arbitrary parcel.
 */
export function pickTargetParcel(
  parcels: ParcelRow[],
  target: { latitude: number; longitude: number; line1: string | null },
): ParcelRow | null {
  if (parcels.length === 0) return null;

  if (target.line1) {
    const wanted = canonicalizeStreet(target.line1);
    const byAddress = parcels.find(
      (p) => p.address_line1 && canonicalizeStreet(p.address_line1) === wanted,
    );
    if (byAddress) return byAddress;
  }

  const point: [number, number] = [target.longitude, target.latitude];
  const byContainment = parcels.find((p) => {
    if (!p.geometry) return false;
    const geom = wktToGeoJSON(p.geometry);
    return geom != null && pointInGeometry(point, geom);
  });
  if (byContainment) return byContainment;

  return null;
}
